import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { FormManagementTabId } from '@/contracts/form-management'
import type { FormFolderContract, FormUiContract } from '@/contracts/questionnaire'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import type { FormStatus } from '@/types/form'
import {
  Archive,
  ArchiveRestore,
  Copy,
  ExternalLink,
  FilePenLine,
  Folder,
  Link2,
  Megaphone,
  MoreHorizontal,
  Settings,
} from 'lucide-react'

type PublicationStatus = Extract<FormStatus, 'draft' | 'published'>
type MenuStatus = Extract<FormStatus, 'draft' | 'published' | 'archived'>

type FormQuickActionsMenuProps = {
  form: FormUiContract
  folders: FormFolderContract[]
  canManageForms: boolean
  onMoveToFolder: (formId: string, folderId: string) => void
  onManage: (form: FormUiContract, tab?: FormManagementTabId) => void
  onDuplicate: (form: FormUiContract) => void
  onStatusChange: (form: FormUiContract, status: MenuStatus) => void
  onPreview: (formId: string) => void
  onEdit: (formId: string) => void
}

export function FormQuickActionsMenu({
  form,
  folders,
  canManageForms,
  onMoveToFolder,
  onManage,
  onDuplicate,
  onStatusChange,
}: FormQuickActionsMenuProps) {
  const disabledReason = '需要管理員權限'
  const isArchived = form.status === 'archived'
  const nextPublicationStatus: PublicationStatus =
    form.status === 'published' ? 'draft' : 'published'
  const publicUrl = buildPublicFormUrl(form.id)

  function copyLink() {
    void navigator.clipboard?.writeText(publicUrl)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`${form.title} 選單`}>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>內容與公開</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}>
          <ExternalLink className="size-4" />
          填寫問卷
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={copyLink}>
          <Link2 className="size-4" />
          問卷連結
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>管理功能</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onManage(form, 'completionPage')}>
          <Settings className="size-4" />
          管理
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>問卷操作</DropdownMenuLabel>
        {!canManageForms ? (
          <div className="px-2 py-1.5 text-xs leading-5 text-slate-500">
            只讀檢視：複製、移動與狀態切換需要管理員權限。
          </div>
        ) : null}
        <DropdownMenuItem
          disabled={!canManageForms}
          title={!canManageForms ? disabledReason : '複製問卷'}
          onSelect={() => onDuplicate(form)}
        >
          <Copy className="size-4" />
          複製問卷
          {!canManageForms && (
            <span className="ml-auto text-xs text-slate-400">{disabledReason}</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuLabel>狀態操作</DropdownMenuLabel>
        <DropdownMenuItem
          disabled={!canManageForms || isArchived}
          title={
            !canManageForms
              ? disabledReason
              : isArchived
                ? '請先在管理工作區還原封存'
                : nextPublicationStatus === 'published'
                  ? '改為公開'
                  : '改為草稿'
          }
          onSelect={() => {
            if (!isArchived) onStatusChange(form, nextPublicationStatus)
          }}
        >
          {nextPublicationStatus === 'published' ? (
            <Megaphone className="size-4" />
          ) : (
            <FilePenLine className="size-4" />
          )}
          {isArchived
            ? '已封存，需先還原'
            : nextPublicationStatus === 'published'
              ? '改為公開'
              : '改為草稿'}
          {!canManageForms && (
            <span className="ml-auto text-xs text-slate-400">{disabledReason}</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={!canManageForms}
            title={!canManageForms ? disabledReason : '移到資料夾'}
          >
            <Folder className="size-4" />
            移到資料夾
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {folders.map((folder) => (
              <DropdownMenuItem key={folder.id} onSelect={() => onMoveToFolder(form.id, folder.id)}>
                <Folder className="size-4" />
                {folder.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canManageForms}
          title={!canManageForms ? disabledReason : isArchived ? '解除封存' : '設為封存'}
          onSelect={() => onStatusChange(form, isArchived ? 'draft' : 'archived')}
        >
          {isArchived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
          {isArchived ? '解除封存' : '設為封存'}
          {!canManageForms && (
            <span className="ml-auto text-xs text-slate-400">{disabledReason}</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
